import { AccountModel, fetchRoute, getBrandUrl, Icon, TBrand, UserModel } from "@cakemail-org/ui-components-v2"
import { TSubNavItem } from "@cakemail-org/ui-components-v2/dist/esm/components/subNav/types"
import { t } from "i18next"
import { getLanguageUrlSegment } from "../../data/language"
import { hasAccessToBilling } from "../../utils/billing"
import { EFeatureFlags, EPageCustomization, hasFeatureFlag, hasPageElementAccess } from "../../utils/limits_features"
import { ERoutes } from "../../utils/url"
import { TAppMenuItems } from "./types"


// Menu builder utility for managing conditional insertions
class MenuBuilder {
    private items: TAppMenuItems[] = []

    constructor(baseItems: TAppMenuItems[] = []) {
        this.items = [...baseItems]
    }

    // Find index of a named item in the current built menu
    findItemIndex(itemName: string): number {
        const currentItems = this.items
        return currentItems.findIndex(item => item.name === itemName)
    }

    // Insert items after a named menu item
    insertAfter(targetName: string, items: TAppMenuItems[], condition: boolean = true) {
        if (!condition) return this

        const targetIndex = this.findItemIndex(targetName)
        if (targetIndex !== -1) {
            this.items.splice(targetIndex + 1, 0, ...items)
        } else {
            // If target not found, append to the end
            this.append(items, condition)
        }
        return this
    }

    // Insert items before a named menu item
    insertBefore(targetName: string, items: TAppMenuItems[], condition: boolean = true) {
        if (!condition) return this

        const targetIndex = this.findItemIndex(targetName)
        if (targetIndex !== -1) {
            this.items.splice(targetIndex, 0, ...items)
        }
        return this
    }

    // Append items at the end of the menu
    append(items: TAppMenuItems[], condition: boolean = true) {
        if (!condition) return this

        this.items.push(...items)
        return this
    }

    // Insert items at specific index
    insertAt(index: number, items: TAppMenuItems[], condition: boolean = true) {
        if (!condition) return this

        this.items.splice(index, 0, ...items)
        return this
    }

    // Build the final menu with all insertions applied
    build(): TAppMenuItems[] {
        return this.items
    }
}


export function getMenuSubNav(): TSubNavItem[] {
    return [
        {
            name: "home",
            text: <Icon name="Home18" />
        },
        {
            name: "add",
            text: <Icon name="Add18" />
        },
        {
            name: "search",
            text: <Icon name="Search18" />
        }
    ]
}

export function getMenuHome({ account, partnerBrand, user }: { account: AccountModel, partnerBrand: TBrand, user: UserModel }): TAppMenuItems[] {

    const disabledAutomation = hasPageElementAccess({ pageElement: EPageCustomization.automationsDisabled, partnerBrand })
    const disabledForms = hasPageElementAccess({ pageElement: EPageCustomization.formsDisabled, partnerBrand })
    const emailAPIGuidesEnabled = hasPageElementAccess({ pageElement: EPageCustomization.emailAPIGuideEnabled, partnerBrand })
    const enabledEmailApi = hasFeatureFlag({ flag: EFeatureFlags.emailAPI, partnerBrand })
    const userLanguage = user?.language || "en_US"

    const languageUrlSegment = getLanguageUrlSegment(userLanguage, partnerBrand)

    // Documentation items - path properties removed to prevent default navigation behavior
    // Navigation is handled by custom menuItemClick handlers that open links in new tabs
    const emailAPIDocumentationItems: TAppMenuItems[] = [
        {
            name: "doc-started",
            // path: `https://cakemail.dev/${languageUrlSegment}/guides/getting-started-email-api`,
            text: t("general-getting-started"),
            defaultItemIcon: "Link10"
        },
        {
            name: "doc-activity",
            // path: `https://cakemail.dev/${languageUrlSegment}/api/email-api`,
            text: t("general-api-references"),
            defaultItemIcon: "Link10"
        }
    ]

    // Use MenuBuilder to handle conditional insertions
    const menuBuilder = new MenuBuilder([])

    if (!account.isPartnerOrOrganization()) {
        menuBuilder.append([
            {
                tooltipText: t("nav-dashboard-tooltip"),
                icon: "Dashboard18",
                text: t("general-home"),
                name: "dashboard",
                path: "#" + fetchRoute({ route: ERoutes.dashboard }),
                defaultItemIcon: false
            },
            {
                tooltipText: t("nav-campaigns-tooltip"),
                icon: "Campaigns18",
                text: t("general-campaign_other"),
                name: "campaigns",
                subItems: [
                    {
                        text: t("general-regular-email"),
                        name: "regularCampaigns",
                        path: "#" + fetchRoute({ route: ERoutes.campaigns }),
                        defaultItemIcon: false
                    },
                    {
                        text: t("general-abtest"),
                        name: "abtestCampaigns",
                        path: "#" + fetchRoute({ route: ERoutes.campaignsABTest }),
                        defaultItemIcon: false
                    },
                    {
                        text: t("general-browse-email-templates"),
                        name: "templateBrowse",
                        path: "#" + fetchRoute({ route: ERoutes.templateBrowse }),
                        defaultItemIcon: false
                    }
                ]
            },
            {
                tooltipText: t("nav-audience-tooltip"),
                icon: "User18",
                text: t("general-audience_one"),
                name: "audience",
                subItems: [
                    {
                        text: t("general-all-contacts"),
                        name: "allContacts",
                        path: "#" + fetchRoute({ route: ERoutes.contacts }),
                        defaultItemIcon: false
                    },
                    {
                        text: t("general-list_other"),
                        name: "lists",
                        path: "#" + fetchRoute({ route: ERoutes.lists }),
                        defaultItemIcon: false
                    },
                    {
                        text: t("general-suppression-list"),
                        name: "suppressionList",
                        path: "#" + fetchRoute({ route: ERoutes.suppressionList }),
                        defaultItemIcon: false
                    }
                ]
            },
            {
                tooltipText: t("nav-content-tooltip"),
                icon: "Templates18",
                text: t("general-resource_other"),
                name: "content",
                subItems: [
                    {
                        text: t("nav-templates-my"),
                        name: "emailTemplates",
                        path: "#" + fetchRoute({ route: ERoutes.templates }),
                        defaultItemIcon: false
                    },
                    {
                        text: t("general-library"),
                        name: "images",
                        path: "#" + fetchRoute({ route: ERoutes.fileManager }),
                        defaultItemIcon: false
                    }
                ]
            }
        ])

        menuBuilder.insertAfter("campaigns", [
            {
                tooltipText: t("nav-automations-tooltip"),
                icon: "Flow18",
                text: t("general-automation_other"),
                name: "automations",
                path: "#" + fetchRoute({ route: ERoutes.automations }),
                defaultItemIcon: false
                /*subItems: [
                    {
                        text: t("general-drip-campaigns"),
                        name: "dripCampaigns",
                        path: "#" + fetchRoute({ route: ERoutes.automations }),
                        defaultItemIcon: false,
                    },
                ]*/
            },
        ], !disabledAutomation)

        menuBuilder.insertAfter("automations", [
            {
                tooltipText: t("nav-forms-tooltip"),
                icon: "Field18",
                text: t("general-form_other"),
                name: "forms-pages",
                subItems: [
                    {
                        text: t("general-legacy"),
                        name: "legacyForms",
                        path: "#" + fetchRoute({ route: ERoutes.legacyForms }),
                        defaultItemIcon: false
                    },
                    {
                        text: t("general-regular"),
                        name: "forms",
                        path: "#" + fetchRoute({ route: ERoutes.forms }),
                        defaultItemIcon: false
                    },
                    {
                        text: t("general-popup"),
                        name: "popups",
                        path: "#" + fetchRoute({ route: ERoutes.popups }),
                        defaultItemIcon: false
                    },
                    {
                        text: t("general-page_other"),
                        name: "pages",
                        path: "#" + fetchRoute({ route: ERoutes.pages }),
                        defaultItemIcon: false
                    },
                ]
            }
        ], !disabledForms)

    }

    if (account.isPartnerOrOrganization()) {
        menuBuilder.append([
            {
                tooltipText: t("nav-accounts-tooltip"),
                icon: "Dashboard18",
                text: t("general-account_other"),
                name: "accounts",
                path: "#" + fetchRoute({ route: ERoutes.accounts }),
                defaultItemIcon: false
            },
            {
                tooltipText: t("nav-content-tooltip"),
                icon: "Templates18",
                text: t("general-resource_other"),
                name: "content",
                subItems: [
                    {
                        text: t("nav-templates-my"),
                        name: "emailTemplates",
                        path: "#" + fetchRoute({ route: ERoutes.templates }),
                        defaultItemIcon: false
                    }
                ]
            }
        ])

        menuBuilder.insertAfter("dashboard", [
            {
                tooltipText: t("nav-audience-tooltip"),
                icon: "User18",
                text: t("general-audience_one"),
                name: "audience",
                subItems: [
                    {
                        text: t("general-all-contacts"),
                        name: "allContacts",
                        path: "#" + fetchRoute({ route: ERoutes.contacts }),
                        defaultItemIcon: false
                    }
                ]
            }
        ], account.hasAccessTo({ feature: "use_email_api" }))
    }

    menuBuilder.insertAfter("audience", [
        {
            tooltipText: t("nav-email-api-tooltip"),
            icon: "Code18",
            text: t("general-email-api"),
            name: "email-api",
            subItems: [
                {
                    name: "metrics",
                    path: "#" + fetchRoute({ route: ERoutes.emailMetrics }),
                    text: t("general-metrics-trends"),
                    defaultItemIcon: false
                },
                {
                    name: "deliverability",
                    path: "#" + fetchRoute({ route: ERoutes.emailDeliverability }),
                    text: t("general-deliverability"),
                    defaultItemIcon: false
                },
                /*{
                    name: "reports",
                    path: "#" + fetchRoute({ route: ERoutes.emailReports }),
                    text: t("email-api-header-reports-heading"),
                    icon: "Sends24",
                },*/
                {
                    name: "requestLogs",
                    path: "#" + fetchRoute({ route: ERoutes.emailRequestLog }),
                    text: t("general-requestLog"),
                    defaultItemIcon: false
                },
                ...(emailAPIGuidesEnabled ? emailAPIDocumentationItems : [])
            ]
        }
    ], enabledEmailApi)

    return menuBuilder.build()
}


export function getMenuSettings({ account, partnerBrand, user }: { account: AccountModel, partnerBrand: TBrand, user: UserModel }): TAppMenuItems[] {
    const helpUrl = getBrandUrl({
        mainKey: "url",
        elKey: "help_url",
        brand: partnerBrand,
        locale: user?.language
    })

    const menuBuilder = new MenuBuilder([
        {
            icon: "Organization18",
            text: t("general-org-profile"),
            name: "profile",
            path: "#" + fetchRoute({ route: ERoutes.accountProfile }),
            defaultItemIcon: false
        },
        {
            icon: "User18",
            text: t("general-user_other"),
            name: "users",
            path: "#" + fetchRoute({ route: ERoutes.users }),
            defaultItemIcon: false
        },
        {
            icon: "EmailAddress18",
            text: t("general-senders_domain"),
            name: "sender&domain",
            path: "#" + fetchRoute({ route: ERoutes.senders }),
            defaultItemIcon: false
        },
        {
            icon: "Branded18",
            text: t("general-brand-kit"),
            name: "brandKit",
            path: "#" + fetchRoute({ route: ERoutes.brand }),
            defaultItemIcon: false
        }
    ])

    menuBuilder.append([
        {
            icon: "Export18",
            text: t("general-export_other"),
            name: "export",
            path: "#" + fetchRoute({ route: ERoutes.export }),
            defaultItemIcon: false
        }], !account.isPartnerOrOrganization())

    /*menuBuilder.append([
        {
            text: t("general-tracking-domain"),
            name: "systemEmails",
            path: "#" + fetchRoute({ route: ERoutes.domainTracking }),
            defaultItemIcon: false,
        },
        {
            text: t("general-system-emails"),
            name: "systemEmails",
            path: "#" + fetchRoute({ route: ERoutes.systemEmails }),
            defaultItemIcon: false,
        }], account.isPartnerOrOrganization())
*/
    menuBuilder.append([
        {
            icon: "Money18",
            text: t("general-plans_billing"),
            name: "plans&billing",
            defaultItemIcon: false
        }], hasAccessToBilling({ account, brand: partnerBrand }))

    menuBuilder.append([
        {
            icon: "Tag18",
            text: t("general-tag-library"),
            name: "tagLibrary",
            defaultItemIcon: false
        }])

    menuBuilder.append([
        {
            icon: "Stripe18",
            text: t("general-stripe-integration"),
            name: "accounts",
            path: "#" + fetchRoute({ route: ERoutes.stripeIntegration }),
            defaultItemIcon: false
        }], hasFeatureFlag({ flag: EFeatureFlags.indiegraf, partnerBrand: partnerBrand }) && !account.isPartnerOrOrganization())

    menuBuilder.append([
        {
            icon: "Integrations18",
            text: t("general-integrations"),
            name: "integrations",
            path: "#" + fetchRoute({ route: ERoutes.integrations }),
            defaultItemIcon: false
        }], hasFeatureFlag({ flag: EFeatureFlags.prismatic, partnerBrand: partnerBrand }) && !account.isPartnerOrOrganization())

    menuBuilder.append([
        {
            text: t("general-help-support"),
            name: "help",
            path: helpUrl,
            icon: "Kb18",
            defaultItemIcon: "Link10"
        }], !!helpUrl)

    menuBuilder.append([
        {
            icon: "Globe18",
            text: t("general-change-language"),
            name: "changeLanguage",
            defaultItemIcon: false
        }])



    return menuBuilder.build()
}

export function getMenuAdd({ account, partnerBrand, user }: { account: AccountModel, partnerBrand: TBrand, user: UserModel }): TAppMenuItems[] {

    const disabledAutomation = hasPageElementAccess({ pageElement: EPageCustomization.automationsDisabled, partnerBrand })
    const disabledForms = hasPageElementAccess({ pageElement: EPageCustomization.formsDisabled, partnerBrand })
    const userCreateDisabled = hasPageElementAccess({ pageElement: EPageCustomization.userCreateDisabled, partnerBrand })

    const menuBuilder = new MenuBuilder()

    if (!account.isPartnerOrOrganization()) {
        menuBuilder.append([
            {
                text: t("general-campaign_other"),
                variant: "subSection",
                name: "subSectionCampaignCreation"
            },
            {
                text: t("general-create-regular-campaign"),
                name: "campaignCreate",
                path: "#" + fetchRoute({ route: ERoutes.campaignsCreate }),
                defaultItemIcon: "Add10"
            },
            {
                text: t("general-create-absplit"),
                name: "campaignCreateABSplit",
                path: "#" + fetchRoute({ route: ERoutes.campaignsABCreate }),
                defaultItemIcon: "Add10"
            },
            {
                text: t("general-create-template"),
                name: "createTemplate",
                defaultItemIcon: "Add10"
            },
            {
                text: t("general-audience_one"),
                variant: "subSection",
                name: "subSectionAudienceCreation"
            },
            {
                text: t("general-create-list"),
                name: "createList",
                path: "#" + fetchRoute({ route: ERoutes.createList }),
                defaultItemIcon: "Add10"
            },
            {
                text: t("general-create-segment_one"),
                name: "createSegment",
                path: "#" + fetchRoute({ route: ERoutes.createSegment }),
                defaultItemIcon: "Add10"
            },
            {
                text: t("general-import-contacts"),
                name: "importContacts",
                path: "#" + fetchRoute({ route: ERoutes.importContacts }),
                defaultItemIcon: "Add10"
            },
            {
                text: t("general-add-contact_one"),
                name: "addContacts",
                path: "#" + fetchRoute({ route: ERoutes.addContacts }),
                defaultItemIcon: "Add10"
            }
        ])


        menuBuilder.insertAfter("addContacts", [
            {
                text: t("general-form_other"),
                variant: "subSection",
                name: "subSectionFormsPagesCreation"
            },
            {
                text: t("general-create-form"),
                name: "createForm",
                path: "#" + fetchRoute({ route: ERoutes.createForm }),
                defaultItemIcon: "Add10"
            }], !disabledForms)

        menuBuilder.insertAfter("addUser", [
            {
                text: t("general-automation_other"),
                variant: "subSection",
                name: "subSectionAutomationCreation"
            },
            {
                text: t("general-create-automation"),
                name: "createAutomation",
                path: "#" + fetchRoute({ route: ERoutes.createAutomation }),
                defaultItemIcon: "Add10"
            }], !disabledAutomation)

        menuBuilder.insertAfter("createAutomation", [
            {
                text: t("general-user_other"),
                variant: "subSection",
                name: "subSectionFormsPagesCreation"
            },
            {
                text: t("general-add-user"),
                name: "addUser",
                path: "#" + fetchRoute({ route: ERoutes.createUser }),
                defaultItemIcon: "Add10"
            }], !userCreateDisabled)
    }


    if (account.isPartnerOrOrganization()) {
        menuBuilder.append([
            {
                text: t("general-account_other"),
                variant: "subSection",
                name: "subSectionAccountCreation"
            },
            {
                text: t("general-create-account"),
                name: "createAccount",
                defaultItemIcon: "Add10"
            }
        ])
        menuBuilder.append([
            {
                text: t("general-audience_one"),
                variant: "subSection",
                name: "subSectionAudienceCreation"
            },
            {
                text: t("general-create-list"),
                name: "createList",
                path: "#" + fetchRoute({ route: ERoutes.createList }),
                defaultItemIcon: "Add10"
            },
            {
                text: t("general-create-segment_one"),
                name: "createSegment",
                path: "#" + fetchRoute({ route: ERoutes.createSegment }),
                defaultItemIcon: "Add10"
            },
            {
                text: t("general-import-contacts"),
                name: "importContacts",
                path: "#" + fetchRoute({ route: ERoutes.importContacts }),
                defaultItemIcon: "Add10"
            },
            {
                text: t("general-add-contact_one"),
                name: "addContacts",
                path: "#" + fetchRoute({ route: ERoutes.addContacts }),
                defaultItemIcon: "Add10"
            }
        ], account.hasAccessTo({ feature: "use_email_api" }))
    }






    return menuBuilder.build()
}